import { Modal, Tabs, Button, Spin } from '@bancobarigui/barihaus';
import { List, Avatar, Image, Empty, message } from 'antd';
import { MessageOutlined, CameraOutlined, UserOutlined, DownloadOutlined } from '@ant-design/icons';
import { VideoSession, VideoSessionHistory, VideoSessionChatMessage, VideoSessionCapture, videoSessionChatService } from '@/services/videoSession.service';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { inspectionClient } from '@/config/api';
import { S3Image } from '@/components/S3Image';

interface VideoSessionHistoryModalProps {
  open: boolean;
  videoSession: VideoSession | null;
  onClose: () => void;
}

export function VideoSessionHistoryModal({
  open,
  videoSession,
  onClose,
}: VideoSessionHistoryModalProps) {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<VideoSessionHistory | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (open && videoSession) {
      loadHistory();
    }
  }, [open, videoSession]);

  const loadHistory = async () => {
    if (!videoSession) return;

    setLoading(true);
    try {
      const historyData = await videoSessionChatService.getHistory(videoSession.id);
      setHistory(historyData);
    } catch (error: any) {
      message.error('Erro ao carregar histórico da sessão');
    } finally {
      setLoading(false);
    }
  };

  const formatMessageTime = (dateString: string) => {
    return dayjs(dateString).format('DD/MM/YYYY HH:mm:ss');
  };

  const formatCaptureTime = (dateString: string) => {
    return dayjs(dateString).format('DD/MM/YYYY HH:mm:ss');
  };

  const handleDownloadPdf = async () => {
    if (!videoSession) return;

    setDownloadingPdf(true);
    try {
      const response = await inspectionClient.get(
        `/api/video-sessions/${videoSession.id}/chat/history/pdf`,
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = `historico-sessao-${videoSession.id}-${dayjs().format('YYYYMMDDHHmmss')}.pdf`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success('PDF gerado e baixado com sucesso!');
    } catch (error: any) {
      message.error('Erro ao gerar PDF do histórico: ' + (error.response?.data?.message || error.message));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const tabItems = [
    {
      key: 'messages',
      label: (
        <span>
          <MessageOutlined /> Mensagens ({history?.totalMessages || 0})
        </span>
      ),
      children: (
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {loading ? (
            <div className="text-center py-8">
              <Spin size="large" />
            </div>
          ) : history && history.messages.length > 0 ? (
            <List
              dataSource={history.messages}
              renderItem={(msg: VideoSessionChatMessage) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar icon={<UserOutlined />} />}
                    title={
                      <div className="flex justify-between items-center">
                        <span>{msg.senderName}</span>
                        <span className="text-xs text-gray-500">
                          {formatMessageTime(msg.sentAt)}
                        </span>
                      </div>
                    }
                    description={
                      <div>
                        <p className="mb-2">{msg.message}</p>
                        {msg.attachmentUrl && (
                          <Image
                            src={msg.attachmentUrl}
                            alt="Anexo"
                            width={200}
                            className="rounded"
                            preview
                          />
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <Empty description="Nenhuma mensagem encontrada" />
          )}
        </div>
      ),
    },
    {
      key: 'captures',
      label: (
        <span>
          <CameraOutlined /> Capturas ({history?.totalCaptures || 0})
        </span>
      ),
      children: (
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {loading ? (
            <div className="text-center py-8">
              <Spin size="large" />
            </div>
          ) : history && history.captures.length > 0 ? (
            <div className="grid grid-cols-3 gap-4">
              {history.captures.map((capture: VideoSessionCapture) => (
                <div key={capture.id} className="relative group">
                  {capture.fileUrl?.startsWith('docman/') ? (
                    <S3Image
                      documentId={capture.fileUrl}
                      alt={capture.description || capture.fileName}
                      className="w-full h-48 object-cover rounded"
                      preview={true}
                      {...(capture.entityId && { entityId: capture.entityId })}
                      {...(capture.documentType && { documentType: capture.documentType })}
                      {...(capture.fileName && { documentName: capture.fileName })}
                    />
                  ) : (
                    <Image
                      src={capture.fileUrl}
                      alt={capture.description || capture.fileName}
                      className="w-full h-48 object-cover rounded"
                      preview
                    />
                  )}
                  <div className="mt-2">
                    <p className="text-xs text-gray-500">
                      {formatCaptureTime(capture.capturedAt)}
                    </p>
                    {capture.description && (
                      <p className="text-sm text-gray-700 mt-1 truncate">
                        {capture.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Empty description="Nenhuma captura encontrada" />
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={`Histórico: ${videoSession?.title || 'Sessão de Vídeo'}`}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="download" buttonColor="primary" icon={<DownloadOutlined />} onClick={handleDownloadPdf} loading={downloadingPdf}>
          Baixar PDF
        </Button>,
        <Button key="close" onClick={onClose}>
          Fechar
        </Button>,
      ]}
      width={800}
    >
      {history && (
        <div className="mb-4 text-sm text-gray-600">
          <p>
            <strong>Status:</strong> {videoSession?.statusDescription || 'N/A'}
          </p>
          {history.actualStartTime && (
            <p>
              <strong>Início:</strong> {dayjs(history.actualStartTime).format('DD/MM/YYYY HH:mm:ss')}
            </p>
          )}
          {history.actualEndTime && (
            <p>
              <strong>Fim:</strong> {dayjs(history.actualEndTime).format('DD/MM/YYYY HH:mm:ss')}
            </p>
          )}
        </div>
      )}

      <Tabs items={tabItems} />
    </Modal>
  );
}


